import { css } from '@emotion/react'
import { Button, HFlow, Icon, Text, Theme, Tooltip, useTheme, VFlow } from 'bold-ui'
import { SkinProps } from 'bold-ui/lib/components/Button/Button/ButtonSkins'
import { HorarioAgendaSelectFieldModel, LotacaoAgendaSelectFieldModel } from 'components/form'
import { usePecField } from 'components/form/core/hooks/useField'
import { useFlags } from 'config/useFlagsContext'
import { AcessoCbo } from 'graphql/types.generated'
import { useState } from 'react'
import { isUndefinedOrNull } from 'util/checks'
import { formatHoursMinutes, formatWeekdayDayMonthYear } from 'util/date/formatDate'
import { MetaPath } from 'util/metaPath'
import { isObjectDeepFalsy } from 'util/object'

import { ProntuarioCard } from '../../../aside/ProntuarioCard'
import { AgendarProximaConsultaForm } from './AgendarProximaConsultaForm'
import {
  agendamentoPropsRecord,
  AgendarConsultaSectionModel,
  ProximaConsultaTypeEnum,
} from './model-agendarConsultaAtendimento'

interface AgendarProximaConsultaCardProps {
  name: MetaPath<AgendarConsultaSectionModel>
  type: ProximaConsultaTypeEnum
  lotacaoSugerida?: LotacaoAgendaSelectFieldModel | null
  dataSugerida: LocalDate
  cbosAcesso?: AcessoCbo[]
  dataTestId?: string
}

export function AgendarProximaConsultaCard(props: Readonly<AgendarProximaConsultaCardProps>) {
  const { name, type, lotacaoSugerida, dataSugerida, cbosAcesso, dataTestId } = props

  const [isFormOpen, setIsFormOpen] = useState(false)

  const theme = useTheme()
  const styles = createStyles(theme)

  const {
    input: { value: agendamentoProximaConsultaValues },
    tools: { resetToInitialValue },
  } = usePecField({ name, subscription: { value: true } })

  const { HORARIO_FINAL_AGENDAMENTO_ENABLED } = useFlags()

  const isFormFilled = !isObjectDeepFalsy(agendamentoProximaConsultaValues)

  const initialValues: AgendarConsultaSectionModel = isFormFilled
    ? agendamentoProximaConsultaValues
    : {
        lotacao: lotacaoSugerida,
        data: dataSugerida,
      }

  const agendamentoDescription = agendamentoPropsRecord[type].description
  const { lotacao } = initialValues

  const { dataFormatted, horarioFormatted, equipeFormatted } = formatValues(
    initialValues,
    HORARIO_FINAL_AGENDAMENTO_ENABLED
  )
  const { tooltipEditar, tooltipAgendar, tooltipExcluir } = getTooltipsButtons(isFormFilled, isFormOpen)

  const handleEditar = () => setIsFormOpen(true)
  const handleExcluir = () => resetToInitialValue()

  const cardTitle = (
    <HFlow alignItems='center'>
      <Icon icon='calendarFilled' fill='primary' size={1} />
      {agendamentoDescription} - {dataFormatted}
      {horarioFormatted}
    </HFlow>
  )
  const semLotacaoComAgenda = isUndefinedOrNull(lotacao)

  const cardTitleIcon = (
    <HFlow>
      <Tooltip text={tooltipEditar}>
        <Button size='small' skin='ghost' disabled={!isFormFilled} onClick={handleEditar}>
          <Icon icon='penOutline' size={1.25} />
        </Button>
      </Tooltip>
      <Tooltip text={tooltipExcluir}>
        <Button size='small' skin='ghost' disabled={!isFormFilled} onClick={handleExcluir}>
          <Icon icon='trashOutline' size={1.25} />
        </Button>
      </Tooltip>
    </HFlow>
  )

  return (
    <ProntuarioCard title={cardTitle} content={cardTitleIcon}>
      <VFlow vSpacing={0} data-testid={dataTestId}>
        <HFlow alignItems='flex-end' justifyContent='space-between' style={styles.container}>
          {semLotacaoComAgenda ? (
            <Text fontStyle='italic' style={styles.semProfDescription}>
              Não existem profissionais com agenda configurada
            </Text>
          ) : (
            <VFlow vSpacing={0}>
              <Text fontWeight='bold'>{lotacao?.profissional?.nome}</Text>
              <HFlow alignItems='flex-start' justifyContent='space-between'>
                <Text style={styles.profDescription}>
                  <Text fontWeight='bold'>{lotacao?.cbo?.nome}</Text> | {equipeFormatted}
                </Text>
              </HFlow>
            </VFlow>
          )}
          <Tooltip text={tooltipAgendar}>
            <Button
              size='small'
              disabled={isFormFilled || isFormOpen || semLotacaoComAgenda}
              onClick={handleEditar}
              {...getAgendarButtonSkinProps(isFormFilled, isFormOpen)}
            >
              {isFormFilled && <Icon icon='checkDefault' style={styles.editIcon} />}
              {isFormFilled ? 'Selecionado' : 'Agendar'}
            </Button>
          </Tooltip>
        </HFlow>
        {isFormOpen && (
          <AgendarProximaConsultaForm
            name={name}
            initialValues={initialValues}
            setIsFormOpen={setIsFormOpen}
            cbosAcesso={cbosAcesso}
          />
        )}
      </VFlow>
    </ProntuarioCard>
  )
}

function formatValues(
  values: Pick<AgendarConsultaSectionModel, 'lotacao' | 'data' | 'horario'>,
  showHorarioFinal: boolean
) {
  const { lotacao, data, horario } = values

  const dataFormatted = formatWeekdayDayMonthYear(data).capitalize()
  const horarioFormatted = formatHorario(horario, showHorarioFinal)
  const equipeFormatted = lotacao?.equipe ? `Equipe ${lotacao.equipe.ine} - ${lotacao.equipe.nome}` : 'Sem equipe'

  return { dataFormatted, horarioFormatted, equipeFormatted }
}

function formatHorario(horario: HorarioAgendaSelectFieldModel | undefined, showHorarioFinal: boolean): string {
  if (!horario?.inicial) {
    return ''
  }

  if (showHorarioFinal && !!horario.final) {
    return ` das ${formatHoursMinutes(horario.inicial.getTime())} às ${formatHoursMinutes(horario.final.getTime())}`
  }

  return ` às ${formatHoursMinutes(horario.inicial.getTime())}`
}

function getTooltipsButtons(isFormFilled: boolean, isFormOpen: boolean) {
  const tooltipEditar = isFormFilled ? 'Editar' : 'Preencha os dados do agendamento'
  const tooltipAgendar = isFormOpen
    ? 'Preencha os dados do agendamento'
    : isFormFilled
    ? 'Dados do agendamento já foram preenchidos'
    : ''
  const tooltipExcluir = isFormFilled ? 'Excluir' : 'Preencha os dados do agendamento'
  return { tooltipEditar, tooltipAgendar, tooltipExcluir }
}

function getAgendarButtonSkinProps(isFormFilled: boolean, isFormOpen: boolean): SkinProps {
  return isFormFilled
    ? { kind: 'normal', skin: 'ghost' }
    : isFormOpen
    ? { kind: 'normal', skin: 'outline' }
    : { kind: 'primary', skin: 'default' }
}

const createStyles = (theme: Theme) => ({
  container: css`
    padding: 0.75rem 1rem 0.5rem;
  `,
  semProfDescription: css`
    margin: 0 0 0.3rem;
  `,
  profDescription: css`
    color: ${theme.pallete.gray.c20};
  `,
  editIcon: css`
    margin-right: 0.25rem;
  `,
})
